/**
 * GraphQL Mutation Hooks
 *
 * Replaces REST API calls with GraphQL mutations for consistency with subscriptions.
 * Uses urql's gql at runtime for graphcache compatibility.
 */

import { useMutation, gql } from 'urql';
import createDebug from 'debug';
import type {
	UpdateLinkInput,
	EnrichOptionsInput,
	BatchOperationsInput,
	BulkEnrichInput,
	CreateQueueInput,
	UpdateQueueInput,
} from '@/graphql/generated/graphql';
import { encodeGlobalId } from '@/lib/global-id';

// Flexible input types that accept numeric IDs (converted internally to global IDs)
type FlexibleId = number | string;
interface FlexibleBatchOperationsInput {
	archive?: FlexibleId[];
	unarchive?: FlexibleId[];
	enrich?: FlexibleId[];
	move?: { queueId: FlexibleId; ids: FlexibleId[] };
	categorize?: { category: string; ids: FlexibleId[] };
	snooze?: { duration: number; ids: FlexibleId[] };
	unsnooze?: FlexibleId[];
}

const debug = createDebug('app:mutations');

// Mutation documents - parsed at runtime for graphcache compatibility
const UpdateLinkMutation = gql`
	mutation UpdateLink($id: ID!, $input: UpdateLinkInput!) {
		updateLink(id: $id, input: $input) {
			link {
				id
				url
				title
				description
				queueId
				thumbnailUrl
				isEnriching
				updatedAt
				tags(first: 50) {
					edges {
						node {
							id
							name
							color
							icon
							isNsfw
							isCategory
							isPersonal
						}
					}
				}
			}
			errors {
				field
				message
			}
		}
	}
`;

const ArchiveLinkMutation = gql`
	mutation ArchiveLink($id: ID!) {
		archiveLink(id: $id) {
			link {
				id
				archivedAt
			}
			success
			errors {
				field
				message
			}
		}
	}
`;

const UnarchiveLinkMutation = gql`
	mutation UnarchiveLink($id: ID!) {
		unarchiveLink(id: $id) {
			link {
				id
				archivedAt
			}
			success
			errors {
				field
				message
			}
		}
	}
`;

const AssignLinkToQueueMutation = gql`
	mutation AssignLinkToQueue($linkId: ID!, $queueId: ID!) {
		assignLinkToQueue(linkId: $linkId, queueId: $queueId) {
			link {
				id
				queueId
				queue {
					id
					slug
					name
				}
			}
			previousQueueId
			errors {
				field
				message
			}
		}
	}
`;

const EnrichLinkMutation = gql`
	mutation EnrichLink($id: ID!, $options: EnrichOptionsInput) {
		enrichLink(id: $id, options: $options) {
			link {
				id
				isEnriching
			}
			queued
			errors {
				field
				message
			}
		}
	}
`;

const SubmitUrlMutation = gql`
	mutation SubmitUrl($url: String!, $source: String) {
		submitUrl(url: $url, source: $source) {
			queued
			message
			existingLink {
				id
				url
				title
			}
		}
	}
`;

const BatchOperationsMutation = gql`
	mutation BatchOperations($input: BatchOperationsInput!) {
		batchOperations(input: $input) {
			results {
				enriched
				moved
				categorized
				archived
				unarchived
				snoozed
				unsnoozed
			}
			errors {
				field
				message
			}
		}
	}
`;

const BulkEnrichLinksMutation = gql`
	mutation BulkEnrichLinks($input: BulkEnrichInput!) {
		bulkEnrichLinks(input: $input) {
			queued
			errors {
				field
				message
			}
		}
	}
`;

const SnoozeLinkMutation = gql`
	mutation SnoozeLink($id: ID!, $input: SnoozeInput!) {
		snoozeLink(id: $id, input: $input) {
			link {
				id
				snoozedUntil
			}
			success
			errors {
				field
				message
			}
		}
	}
`;

const UnsnoozeLinkMutation = gql`
	mutation UnsnoozeLink($id: ID!) {
		unsnoozeLink(id: $id) {
			link {
				id
				snoozedUntil
			}
			success
			errors {
				field
				message
			}
		}
	}
`;

const AddTagsToLinkMutation = gql`
	mutation AddTagsToLink($linkId: ID!, $tags: [String!]!, $isPersonal: Boolean) {
		addTagsToLink(linkId: $linkId, tags: $tags, isPersonal: $isPersonal) {
			link {
				id
				tags(first: 50) {
					edges {
						node {
							id
							name
							color
							icon
							isNsfw
							isCategory
							isPersonal
						}
					}
				}
			}
			addedTags {
				id
				name
				color
				icon
				isNsfw
				isCategory
				isPersonal
			}
			errors {
				field
				message
			}
		}
	}
`;

const RemoveTagFromLinkMutation = gql`
	mutation RemoveTagFromLink($linkId: ID!, $tagId: ID!, $isPersonal: Boolean) {
		removeTagFromLink(linkId: $linkId, tagId: $tagId, isPersonal: $isPersonal) {
			link {
				id
				tags(first: 50) {
					edges {
						node {
							id
							name
							color
							icon
							isNsfw
							isCategory
							isPersonal
						}
					}
				}
			}
			removedTag {
				id
				name
			}
			errors {
				field
				message
			}
		}
	}
`;

const CreateQueueMutation = gql`
	mutation CreateQueue($input: CreateQueueInput!) {
		createQueue(input: $input) {
			queue {
				id
				slug
				name
				color
				icon
				position
				linkCount
			}
			errors {
				field
				message
			}
		}
	}
`;

const UpdateQueueMutation = gql`
	mutation UpdateQueue($id: ID!, $input: UpdateQueueInput!) {
		updateQueue(id: $id, input: $input) {
			queue {
				id
				slug
				name
				color
				icon
				position
				linkCount
			}
			errors {
				field
				message
			}
		}
	}
`;

const DeleteQueueMutation = gql`
	mutation DeleteQueue($id: ID!) {
		deleteQueue(id: $id) {
			deletedId
			success
			errors {
				field
				message
			}
		}
	}
`;

// ============================================================================
// Link Mutation Hooks
// ============================================================================

export function useUpdateLinkMutation() {
	const [result, executeMutation] = useMutation(UpdateLinkMutation);

	async function updateLinkAsync(
		id: number,
		data: Partial<{ url: string; title: string; description: string; categories: string[]; queueId: number }>
	) {
		debug('updateLink id=%d data=%o', id, data);

		const input: UpdateLinkInput = {};
		if (data.url !== undefined) input.url = data.url;
		if (data.title !== undefined) input.title = data.title;
		if (data.description !== undefined) input.description = data.description;
		if (data.categories !== undefined) input.categories = data.categories;
		if (data.queueId !== undefined) input.queueId = data.queueId;

		const res = await executeMutation({
			id: encodeGlobalId('Link', id),
			input,
		});

		if (res.error) {
			debug('updateLink error: %o', res.error);
			throw res.error;
		}

		if (res.data?.updateLink.errors.length) {
			const err = new Error(res.data.updateLink.errors[0].message);
			debug('updateLink payload error: %s', err.message);
			throw err;
		}

		debug('updateLink success id=%d', id);
		return res.data?.updateLink.link;
	}

	// TanStack Query compatible interface
	function mutate(
		variables: { id: number; data: Partial<{ url: string; title: string; description: string; categories: string[]; queueId: number }> },
		options?: { onSuccess?: (data: unknown) => void; onError?: (error: Error) => void }
	) {
		updateLinkAsync(variables.id, variables.data)
			.then((data) => options?.onSuccess?.(data))
			.catch((error) => options?.onError?.(error));
	}

	async function mutateAsync(
		variables: { id: number; data: Partial<{ url: string; title: string; description: string; categories: string[]; queueId: number }> }
	) {
		return updateLinkAsync(variables.id, variables.data);
	}

	return {
		mutate,
		mutateAsync,
		isPending: result.fetching,
		error: result.error,
	};
}

export function useArchiveLinkMutation() {
	const [result, executeMutation] = useMutation(ArchiveLinkMutation);

	async function archiveLink(id: number) {
		debug('archiveLink id=%d', id);

		const res = await executeMutation({
			id: encodeGlobalId('Link', id),
		});

		if (res.error) {
			debug('archiveLink error: %o', res.error);
			throw res.error;
		}

		if (res.data?.archiveLink.errors.length) {
			const err = new Error(res.data.archiveLink.errors[0].message);
			debug('archiveLink payload error: %s', err.message);
			throw err;
		}

		debug('archiveLink success id=%d', id);
		return res.data?.archiveLink.link;
	}

	return {
		mutate: archiveLink,
		mutateAsync: archiveLink,
		isPending: result.fetching,
		error: result.error,
	};
}

export function useUnarchiveLinkMutation() {
	const [result, executeMutation] = useMutation(UnarchiveLinkMutation);

	async function unarchiveLink(id: number) {
		debug('unarchiveLink id=%d', id);

		const res = await executeMutation({
			id: encodeGlobalId('Link', id),
		});

		if (res.error) {
			debug('unarchiveLink error: %o', res.error);
			throw res.error;
		}

		if (res.data?.unarchiveLink.errors.length) {
			const err = new Error(res.data.unarchiveLink.errors[0].message);
			debug('unarchiveLink payload error: %s', err.message);
			throw err;
		}

		debug('unarchiveLink success id=%d', id);
		return res.data?.unarchiveLink.link;
	}

	return {
		mutate: unarchiveLink,
		mutateAsync: unarchiveLink,
		isPending: result.fetching,
		error: result.error,
	};
}

export function useAssignQueueMutation() {
	const [result, executeMutation] = useMutation(AssignLinkToQueueMutation);

	async function assignQueueAsync(linkId: number, queueId: number) {
		debug('assignQueue linkId=%d queueId=%d', linkId, queueId);

		const res = await executeMutation({
			linkId: encodeGlobalId('Link', linkId),
			queueId: encodeGlobalId('Queue', queueId),
		});

		if (res.error) {
			debug('assignQueue error: %o', res.error);
			throw res.error;
		}

		if (res.data?.assignLinkToQueue.errors.length) {
			const err = new Error(res.data.assignLinkToQueue.errors[0].message);
			debug('assignQueue payload error: %s', err.message);
			throw err;
		}

		debug('assignQueue success linkId=%d queueId=%d', linkId, queueId);
		return res.data?.assignLinkToQueue.link;
	}

	// TanStack Query compatible interface
	function mutate(
		variables: { id: number; queueId: number },
		callbacks?: { onSuccess?: (data: unknown) => void; onError?: (error: Error) => void }
	) {
		assignQueueAsync(variables.id, variables.queueId)
			.then((data) => callbacks?.onSuccess?.(data))
			.catch((error) => callbacks?.onError?.(error));
	}

	async function mutateAsync(variables: { id: number; queueId: number }) {
		return assignQueueAsync(variables.id, variables.queueId);
	}

	return {
		mutate,
		mutateAsync,
		isPending: result.fetching,
		error: result.error,
	};
}

export function useEnrichLinkMutation() {
	const [result, executeMutation] = useMutation(EnrichLinkMutation);

	async function enrichLinkAsync(id: number, options?: EnrichOptionsInput) {
		debug('enrichLink id=%d options=%o', id, options);

		const res = await executeMutation({
			id: encodeGlobalId('Link', id),
			options,
		});

		if (res.error) {
			debug('enrichLink error: %o', res.error);
			throw res.error;
		}

		if (res.data?.enrichLink.errors.length) {
			const err = new Error(res.data.enrichLink.errors[0].message);
			debug('enrichLink payload error: %s', err.message);
			throw err;
		}

		debug('enrichLink success id=%d queued=%s', id, res.data?.enrichLink.queued);
		return res.data?.enrichLink;
	}

	// TanStack Query compatible interface
	function mutate(
		variables: { id: number; options?: EnrichOptionsInput },
		callbacks?: { onSuccess?: (data: unknown) => void; onError?: (error: Error) => void }
	) {
		enrichLinkAsync(variables.id, variables.options)
			.then((data) => callbacks?.onSuccess?.(data))
			.catch((error) => callbacks?.onError?.(error));
	}

	async function mutateAsync(variables: { id: number; options?: EnrichOptionsInput }) {
		return enrichLinkAsync(variables.id, variables.options);
	}

	return {
		mutate,
		mutateAsync,
		isPending: result.fetching,
		error: result.error,
	};
}

export function useSnoozeLinkMutation() {
	const [result, executeMutation] = useMutation(SnoozeLinkMutation);

	async function snoozeLinkAsync(id: number, durationSeconds: number) {
		debug('snoozeLink id=%d duration=%d', id, durationSeconds);

		const res = await executeMutation({
			id: encodeGlobalId('Link', id),
			input: { duration: durationSeconds },
		});

		if (res.error) {
			debug('snoozeLink error: %o', res.error);
			throw res.error;
		}

		if (res.data?.snoozeLink.errors.length) {
			const err = new Error(res.data.snoozeLink.errors[0].message);
			debug('snoozeLink payload error: %s', err.message);
			throw err;
		}

		debug('snoozeLink success id=%d snoozedUntil=%s', id, res.data?.snoozeLink.link?.snoozedUntil);
		return res.data?.snoozeLink.link;
	}

	return {
		mutate: snoozeLinkAsync,
		mutateAsync: snoozeLinkAsync,
		isPending: result.fetching,
		error: result.error,
	};
}

export function useUnsnoozeLinkMutation() {
	const [result, executeMutation] = useMutation(UnsnoozeLinkMutation);

	async function unsnoozeLinkAsync(id: number) {
		debug('unsnoozeLink id=%d', id);

		const res = await executeMutation({
			id: encodeGlobalId('Link', id),
		});

		if (res.error) {
			debug('unsnoozeLink error: %o', res.error);
			throw res.error;
		}

		if (res.data?.unsnoozeLink.errors.length) {
			const err = new Error(res.data.unsnoozeLink.errors[0].message);
			debug('unsnoozeLink payload error: %s', err.message);
			throw err;
		}

		debug('unsnoozeLink success id=%d', id);
		return res.data?.unsnoozeLink.link;
	}

	return {
		mutate: unsnoozeLinkAsync,
		mutateAsync: unsnoozeLinkAsync,
		isPending: result.fetching,
		error: result.error,
	};
}

// ============================================================================
// Quick Add / Submit URL
// ============================================================================

export function useSubmitUrlMutation() {
	const [result, executeMutation] = useMutation(SubmitUrlMutation);

	async function submitUrl(url: string, source?: string) {
		debug('submitUrl url=%s source=%s', url, source);

		const res = await executeMutation({
			url,
			source,
		});

		if (res.error) {
			debug('submitUrl error: %o', res.error);
			throw res.error;
		}

		debug('submitUrl success queued=%s message=%s', res.data?.submitUrl.queued, res.data?.submitUrl.message);
		return res.data?.submitUrl;
	}

	return {
		mutate: submitUrl,
		mutateAsync: submitUrl,
		isPending: result.fetching,
		error: result.error,
	};
}

// ============================================================================
// Batch Operations
// ============================================================================

export function useBatchOperationsMutation() {
	const [result, executeMutation] = useMutation(BatchOperationsMutation);

	async function batchOperations(input: FlexibleBatchOperationsInput) {
		debug('batchOperations input=%o', input);

		// Encode IDs in the input (convert numeric IDs to global IDs)
		const encodedInput: BatchOperationsInput = {};

		if (input.archive) {
			encodedInput.archive = input.archive.map((id) =>
				typeof id === 'number' ? encodeGlobalId('Link', id) : id
			);
		}
		if (input.unarchive) {
			encodedInput.unarchive = input.unarchive.map((id) =>
				typeof id === 'number' ? encodeGlobalId('Link', id) : id
			);
		}
		if (input.enrich) {
			encodedInput.enrich = input.enrich.map((id) =>
				typeof id === 'number' ? encodeGlobalId('Link', id) : id
			);
		}
		if (input.move) {
			encodedInput.move = {
				ids: input.move.ids.map((id) =>
					typeof id === 'number' ? encodeGlobalId('Link', id) : id
				),
				queueId: typeof input.move.queueId === 'number'
					? encodeGlobalId('Queue', input.move.queueId)
					: input.move.queueId,
			};
		}
		if (input.categorize) {
			encodedInput.categorize = {
				ids: input.categorize.ids.map((id) =>
					typeof id === 'number' ? encodeGlobalId('Link', id) : id
				),
				category: input.categorize.category,
			};
		}
		if (input.snooze) {
			encodedInput.snooze = {
				ids: input.snooze.ids.map((id) =>
					typeof id === 'number' ? encodeGlobalId('Link', id) : id
				),
				duration: input.snooze.duration,
			};
		}
		if (input.unsnooze) {
			encodedInput.unsnooze = input.unsnooze.map((id) =>
				typeof id === 'number' ? encodeGlobalId('Link', id) : id
			);
		}

		const res = await executeMutation({ input: encodedInput });

		if (res.error) {
			debug('batchOperations error: %o', res.error);
			throw res.error;
		}

		if (res.data?.batchOperations.errors.length) {
			const err = new Error(res.data.batchOperations.errors[0].message);
			debug('batchOperations payload error: %s', err.message);
			throw err;
		}

		debug('batchOperations success results=%o', res.data?.batchOperations.results);
		return res.data?.batchOperations;
	}

	return {
		mutate: batchOperations,
		mutateAsync: batchOperations,
		isPending: result.fetching,
		error: result.error,
	};
}

export function useBulkEnrichMutation() {
	const [result, executeMutation] = useMutation(BulkEnrichLinksMutation);

	async function bulkEnrich(linkIds: number[], options?: { enrichers?: string[]; force?: boolean }) {
		debug('bulkEnrich linkIds=%o options=%o', linkIds, options);

		const input: BulkEnrichInput = {
			linkIds: linkIds.map((id) => encodeGlobalId('Link', id)),
			enrichers: options?.enrichers,
			force: options?.force,
		};

		const res = await executeMutation({ input });

		if (res.error) {
			debug('bulkEnrich error: %o', res.error);
			throw res.error;
		}

		if (res.data?.bulkEnrichLinks.errors.length) {
			const err = new Error(res.data.bulkEnrichLinks.errors[0].message);
			debug('bulkEnrich payload error: %s', err.message);
			throw err;
		}

		debug('bulkEnrich success queued=%d', res.data?.bulkEnrichLinks.queued);
		return res.data?.bulkEnrichLinks;
	}

	return {
		mutate: bulkEnrich,
		mutateAsync: bulkEnrich,
		isPending: result.fetching,
		error: result.error,
	};
}

// ============================================================================
// Tag Mutations
// ============================================================================

export function useAddTagsToLinkMutation() {
	const [result, executeMutation] = useMutation(AddTagsToLinkMutation);

	async function addTagsToLink(linkId: number, tags: string[], isPersonal?: boolean) {
		debug('addTagsToLink linkId=%d tags=%o isPersonal=%s', linkId, tags, isPersonal);

		const res = await executeMutation({
			linkId: encodeGlobalId('Link', linkId),
			tags,
			isPersonal,
		});

		if (res.error) {
			debug('addTagsToLink error: %o', res.error);
			throw res.error;
		}

		if (res.data?.addTagsToLink.errors.length) {
			const err = new Error(res.data.addTagsToLink.errors[0].message);
			debug('addTagsToLink payload error: %s', err.message);
			throw err;
		}

		debug('addTagsToLink success addedTags=%o', res.data?.addTagsToLink.addedTags);
		return res.data?.addTagsToLink;
	}

	return {
		mutate: addTagsToLink,
		mutateAsync: addTagsToLink,
		isPending: result.fetching,
		error: result.error,
	};
}

export function useRemoveTagFromLinkMutation() {
	const [result, executeMutation] = useMutation(RemoveTagFromLinkMutation);

	async function removeTagFromLink(linkId: number, tagId: number, isPersonal?: boolean) {
		debug('removeTagFromLink linkId=%d tagId=%d isPersonal=%s', linkId, tagId, isPersonal);

		const res = await executeMutation({
			linkId: encodeGlobalId('Link', linkId),
			tagId: encodeGlobalId('Tag', tagId),
			isPersonal,
		});

		if (res.error) {
			debug('removeTagFromLink error: %o', res.error);
			throw res.error;
		}

		if (res.data?.removeTagFromLink.errors.length) {
			const err = new Error(res.data.removeTagFromLink.errors[0].message);
			debug('removeTagFromLink payload error: %s', err.message);
			throw err;
		}

		debug('removeTagFromLink success removedTag=%o', res.data?.removeTagFromLink.removedTag);
		return res.data?.removeTagFromLink;
	}

	return {
		mutate: removeTagFromLink,
		mutateAsync: removeTagFromLink,
		isPending: result.fetching,
		error: result.error,
	};
}

// ============================================================================
// Queue Mutations
// ============================================================================

export function useCreateQueueMutation() {
	const [result, executeMutation] = useMutation(CreateQueueMutation);

	async function createQueue(input: CreateQueueInput) {
		debug('createQueue input=%o', input);

		const res = await executeMutation({ input });

		if (res.error) {
			debug('createQueue error: %o', res.error);
			throw res.error;
		}

		if (res.data?.createQueue.errors.length) {
			const err = new Error(res.data.createQueue.errors[0].message);
			debug('createQueue payload error: %s', err.message);
			throw err;
		}

		debug('createQueue success queue=%o', res.data?.createQueue.queue);
		return res.data?.createQueue.queue;
	}

	return {
		mutate: createQueue,
		mutateAsync: createQueue,
		isPending: result.fetching,
		error: result.error,
	};
}

export function useUpdateQueueMutation() {
	const [result, executeMutation] = useMutation(UpdateQueueMutation);

	async function updateQueue(id: number, input: UpdateQueueInput) {
		debug('updateQueue id=%d input=%o', id, input);

		const res = await executeMutation({
			id: encodeGlobalId('Queue', id),
			input,
		});

		if (res.error) {
			debug('updateQueue error: %o', res.error);
			throw res.error;
		}

		if (res.data?.updateQueue.errors.length) {
			const err = new Error(res.data.updateQueue.errors[0].message);
			debug('updateQueue payload error: %s', err.message);
			throw err;
		}

		debug('updateQueue success queue=%o', res.data?.updateQueue.queue);
		return res.data?.updateQueue.queue;
	}

	return {
		mutate: updateQueue,
		mutateAsync: updateQueue,
		isPending: result.fetching,
		error: result.error,
	};
}

export function useDeleteQueueMutation() {
	const [result, executeMutation] = useMutation(DeleteQueueMutation);

	async function deleteQueue(id: number) {
		debug('deleteQueue id=%d', id);

		const res = await executeMutation({
			id: encodeGlobalId('Queue', id),
		});

		if (res.error) {
			debug('deleteQueue error: %o', res.error);
			throw res.error;
		}

		if (res.data?.deleteQueue.errors.length) {
			const err = new Error(res.data.deleteQueue.errors[0].message);
			debug('deleteQueue payload error: %s', err.message);
			throw err;
		}

		debug('deleteQueue success deletedId=%s', res.data?.deleteQueue.deletedId);
		return res.data?.deleteQueue;
	}

	return {
		mutate: deleteQueue,
		mutateAsync: deleteQueue,
		isPending: result.fetching,
		error: result.error,
	};
}
